import { Injectable, NgZone } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, exhaustMap, catchError, tap, withLatestFrom, mergeMap, filter, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import * as fromAccountActions from '../actions/account.actions';
import { Store, select } from '@ngrx/store';
import { AppState } from '../app.state';
import { ErrorService } from '../../services/error.service';
import { failedTransaction, setLoadingSpinner } from '../actions/shared.actions';
import { AccountService } from 'src/app/services/account.service';
import { ModalController, NavController, ToastController } from '@ionic/angular';
import { RouterNavigatedAction, ROUTER_NAVIGATION } from '@ngrx/router-store';
import { APIResponse } from 'src/app/models/api-response.model';
import { SuccessComponent } from 'src/app/shared/modals/success/success.component';
import { autoLogout } from '../actions/auth.actions';
import { ErrorComponent } from 'src/app/shared/modals/error/error.component';
import { environment } from 'src/environments/environment';
@Injectable()
export class AccountEffects {
  getUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountActions.getUserProfile),
      mergeMap(() => {
        return this.accountService.userProfile().pipe(
          map((response: APIResponse) => {
            console.log('userProfile$', response);

            // Only the Solo Parent ID renders a signature, so the missing-
            // signature block applies to verified SP clients only — PWD and
            // other clients may log in without one.
            const master = response?.details?.masterDetails;
            const isSoloParent = master?.lguSP === 'X';
            const documents = master?.documents ?? [];
            const hasSignature = documents.some(
              (doc: any) => doc?.type?.docName === 'eSignature' && doc?.status === 'active'
            );
            if (isSoloParent && !hasSignature) {
              this.presentNoSignatureModal();
              return autoLogout();
            }

            this.navCtrl.navigateRoot('home');
            return fromAccountActions.updateUserProfileSuccess({ data: response.details });
          })
        );
      })
    )
  );

  private async presentNoSignatureModal() {
    const message = {
      title: 'Signature Required',
      message: 'Your account has no e-signature on file, so your digital ID cannot be displayed yet. Please visit your registration office to add your signature, then log in again.'
    };
    const modal = await this.modalController.create({
      component: ErrorComponent,
      cssClass: 'error-modal',
      componentProps: {
        message
      }
    });
    await modal.present();
  }

  userProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r: RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/dashboard');
      }),
      mergeMap(() => {
        return this.accountService.userProfile().pipe(
          map((response: APIResponse) => {
            console.log('userProfile$', response);
            return fromAccountActions.updateUserProfileSuccess({ data: response.details });
          })
        );
      })
    );
  });

  getDocuments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r: RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/dashboard');
      }),
      mergeMap(() => {
        return this.accountService.getDocumentList().pipe(
          map((response: APIResponse) => {
            console.log('getDocuments$', response);
            return fromAccountActions.getDocumentsSuccess({ data: response.details });
          })
        );
      })
    );
  });

  getQrs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      // Never loaded means the Transport QR card never shows (it waits on isLoaded).
      filter(() => environment.transportQrEnabled),
      filter((r: RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/dashboard');
      }),
      mergeMap(() => {
        return this.accountService.getQrList().pipe(
          map((response: APIResponse) => {
            console.log('getQrs$', response);
            return fromAccountActions.getQRsSuccess({ data: response.details });
          })
        );
      })
    );
  });


  changePassword$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.changePassword),
      exhaustMap((action) =>
        this.accountService.changePassword(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromAccountActions.changePasswordSuccess({ data: response?.details })
          }),
          catchError((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return of(fromAccountActions.changePasswordFailed({ data: response?.error?.message }))
          })
        )
      )
    )
  );

  changePasswordSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.changePasswordSuccess),
      tap(async (action) => {
        const message = {
          title: 'Success',
          message: 'Your password has been successfully changed. Please log in again to continue using your account.'
        }
        const modal = await this.modalController.create({
          component: SuccessComponent,
          cssClass: 'success-modal',
          componentProps: {
            message
          }
        });

        modal.onDidDismiss()
          .then(() => {
            this.store.dispatch(autoLogout());
          });

        await modal.present();
      })
    ),
    { dispatch: false }
  );

  changePasswordFailed$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.changePasswordFailed),
      tap(async (action) => {
        let message = ''
        if (action.data) {
          message = action?.data
        } else {
          message = 'Unkown error ocurred. Please try again later.'
        }

        const modal = await this.modalController.create({
          component: ErrorComponent,
          cssClass: 'error-modal',
          componentProps: {
            message
          }
        });
        return modal.present();
      })
    ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private accountService: AccountService,
    private modalController: ModalController,
    private navCtrl: NavController,
  ) { }
}
